import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColorPicker, parseColor } from '@/Domain/components/Shared/ColorPicker/ColorPicker';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/Domain/components/Shared/Popover/Popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/Domain/components/Shared/Tabs/Tabs';
import { AutosizeTextarea } from '@/Domain/components/Shared/Textarea/Textarea';
import { cn } from '@/Domain/utils/cn';

const isGradient = (value: string): boolean => /gradient\(/i.test(value);

const isValidGradient = (value: string): boolean => {
  if (!value || !isGradient(value)) return false;
  if (typeof window === 'undefined' || typeof CSS === 'undefined' || !CSS.supports) return true;
  return CSS.supports('background-image', value);
};

const extractFirstHex = (value: string): string => {
  const match = /#[0-9a-fA-F]{3,8}/.exec(value);
  return match?.[0] ?? '#000000';
};

const formatTriggerLabel = (
  color: string,
  labels: { transparent: string; gradient: string },
): string => {
  if (!color || color === 'transparent') return labels.transparent;
  if (isGradient(color)) return labels.gradient;
  const parsed = parseColor(color);
  if (!parsed) return labels.transparent;
  const toHex = (n: number) => Math.round(n).toString(16).padStart(2, '0');
  const rgb = `#${toHex(parsed.r)}${toHex(parsed.g)}${toHex(parsed.b)}`.toUpperCase();
  if (parsed.a >= 1) return rgb;
  const a = toHex(parsed.a * 255).toUpperCase();
  return `${rgb}${a}`;
};

type Props = {
  color: string;
  onColorChange: (color: string) => void;
  pickerId: string;
  allowGradient: boolean;
};

type Tab = 'solid' | 'gradient';

export const ColorPickerDropdown = ({ color, onColorChange, pickerId, allowGradient }: Props) => {
  const { t } = useTranslation('color_picker');
  const startsAsGradient = allowGradient && isGradient(color);
  const [tab, setTab] = useState<Tab>(startsAsGradient ? 'gradient' : 'solid');
  const solidStartValue = useMemo(
    () => (isGradient(color) ? extractFirstHex(color) : color),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [solidValue, setSolidValue] = useState(solidStartValue);
  const [gradientDraft, setGradientDraft] = useState(isGradient(color) ? color : '');
  const [gradientError, setGradientError] = useState<string | null>(null);

  useEffect(() => {
    if (isGradient(color)) {
      setGradientDraft(color);
    } else if (color) {
      setSolidValue(color);
    }
  }, [color]);

  const handleSolidChange = (next: string) => {
    setSolidValue(next);
    onColorChange(next);
  };

  const commitGradient = () => {
    const trimmed = gradientDraft.trim();
    if (!trimmed) {
      setGradientError(null);
      return;
    }
    if (!isValidGradient(trimmed)) {
      setGradientError(t('gradient_error'));
      return;
    }
    setGradientError(null);
    if (trimmed !== color) onColorChange(trimmed);
  };

  const livePreview = useMemo(() => {
    const trimmed = gradientDraft.trim();
    if (!trimmed) return color || '#000000';
    if (isValidGradient(trimmed)) return trimmed;
    return color || '#000000';
  }, [gradientDraft, color]);

  const showCheckerPreview = !gradientDraft.trim() && !isGradient(color);

  const isEmpty = !color || color === 'transparent';

  const gradientTextareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (tab !== 'gradient') return;
    const id = requestAnimationFrame(() => gradientTextareaRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [tab]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center h-8 w-28 px-2 rounded-full transition-colors duration-75 outline-none focus-visible:ring-2 focus-visible:ring-ring/40 cursor-pointer',
            isEmpty
              ? 'justify-center border border-dashed border-border text-muted-foreground hover:text-foreground hover:border-foreground/40 bg-transparent'
              : 'justify-between gap-2 border border-border bg-card hover:bg-accent',
          )}
          id={`dropdown-${pickerId}`}
          data-testid={`dropdown-${pickerId}`}
        >
          <span
            data-testid={`dropdown-${pickerId}-swatch`}
            className={isEmpty ? 'sr-only' : 'size-5 rounded-full border border-border/60 shrink-0'}
            style={{ background: color || 'transparent' }}
            aria-hidden={isEmpty ? true : undefined}
          />
          <span className={cn('text-sm truncate', !isEmpty && 'tabular-nums text-foreground')}>
            {isEmpty
              ? t('add_color')
              : formatTriggerLabel(color, {
                  transparent: t('transparent'),
                  gradient: t('gradient'),
                })}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="start" className="p-3 w-auto">
        {allowGradient ? (
          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <TabsList className="mb-3 !flex w-full">
              <TabsTrigger value="solid" className="flex-1 justify-center">
                {t('solid')}
              </TabsTrigger>
              <TabsTrigger value="gradient" className="flex-1 justify-center">
                {t('gradient')}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="solid">
              <ColorPicker value={solidValue} onValueChange={handleSolidChange} />
            </TabsContent>
            <TabsContent value="gradient">
              <div className="flex flex-col gap-3" style={{ width: 280 }}>
                <p className="text-xs text-muted-foreground">{t('gradient_helper')}</p>
                <div
                  className="w-full rounded-lg border border-border"
                  style={{
                    height: 64,
                    ...(showCheckerPreview
                      ? {
                          backgroundImage:
                            'conic-gradient(#bbb 0 25%, #fff 0 50%, #bbb 0 75%, #fff 0)',
                          backgroundSize: '8px 8px',
                        }
                      : { background: livePreview }),
                  }}
                  aria-label={t('gradient_preview')}
                />
                <AutosizeTextarea
                  ref={gradientTextareaRef}
                  minHeight={64}
                  value={gradientDraft}
                  onChange={(e) => {
                    setGradientDraft(e.target.value);
                    if (gradientError) setGradientError(null);
                  }}
                  onBlur={commitGradient}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      commitGradient();
                      (e.currentTarget as HTMLTextAreaElement).blur();
                    }
                  }}
                  placeholder="linear-gradient(90deg, #2532A7 0%, #6273F6 72.89%)"
                  className="font-mono text-xs min-h-[64px]"
                  data-testid={`gradient-input-${pickerId}`}
                />
                {gradientError && <p className="text-xs text-destructive">{gradientError}</p>}
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          <ColorPicker value={solidValue} onValueChange={handleSolidChange} />
        )}
      </PopoverContent>
    </Popover>
  );
};
